import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { StepHeading, StepShell } from "@codel/dova-ds";

import { ApiError } from "@/api/client";
import { useMeContext } from "../../MeApp";
import {
  requestConsentEnvelope,
  type ConsentEnvelopeResponse,
} from "../../consentEnvelope";
import {
  clearEsignLinkToken,
  readEsignLinkToken,
  writeEsignLinkToken,
} from "../../esignLink";
import { Button } from "../components/Button";
import type { StepProps } from "../flowConfig";
import type { OnboardingNavState } from "../useOnboardingForm";

/**
 * S8 — review and sign. The document and the signature; identity was established seven
 * steps ago at S3b.
 *
 * ── WHAT THIS STEP IS ACTUALLY RECONCILING ───────────────────────────────────────────
 * Two facts have to agree before the client can sign, and they live in different places:
 *
 *   the SERVER knows whether this signer already verified — `pending_status`, derived
 *   from their own envelope rows. Durable. Survives everything.
 *
 *   this BROWSER knows the envelope's link token — the plaintext credential that
 *   addresses it. The server stores only a hash and cannot reissue it, so if this tab
 *   lost it, it is gone for good.
 *
 * When both are present the client signs without a second code, which is the entire point
 * of verifying early. When the server says "verified" and the browser has no token, the
 * verification is UNREACHABLE: recovery needs a fresh envelope and therefore a fresh code.
 *
 * That case is the one this step exists to make loud. Left alone it is invisible — the
 * request looks exactly like a first-time signer's, the server mints a second envelope,
 * the client gets a second code and assumes that is the design, and nothing anywhere
 * records that the early verification was wasted. So the client is TOLD, the re-issue is
 * an explicit action rather than a side effect, and the server records it
 * (`EsignEvent.LINK_CONTINUITY_BROKEN`) against the envelope it mints.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * Note what is NOT done here: no client-side "am I verified" flag. The signer session
 * cookie is HttpOnly and scoped to `/esign/{token}`, so this origin can never read it.
 * The only way to know whether a session is live is to ask the API, and the signer app
 * does that when it tries to fetch the document.
 */

// No "ready": reaching it IS the navigation now, so there is no state to sit in.
type Mode = "checking" | "lost_link" | "terminal" | "unavailable";

const SIGNING_RETURN_TO = "/me/onboarding/handoff";

export function S8Consent({ onNext, onBack, currentStepIndex, totalSteps }: StepProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // Whether THIS arrival was `goBack()` stepping backward through the flow, as opposed to
  // a forward `goNext()`/`jumpTo()`/direct-URL arrival. Read once per render, not stored —
  // it describes how this location was reached, not state to persist across it.
  const arrivedViaBack =
    (location.state as OnboardingNavState | null)?.navDirection === "back";
  const { couple, partner } = useMeContext();
  const partnerId = partner?.id ?? "";
  const coupleId = couple?.id ?? "";

  const [mode, setMode] = useState<Mode>("checking");
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  // Bumping this re-runs reconciliation. A transient failure used to leave the screen
  // saying "please try again" with the only button disabled, because the one-shot effect
  // could not run twice and no token had been obtained — copy instructing an action the
  // UI had disabled, recoverable only by navigating away, which nothing said.
  const [attempt, setAttempt] = useState(0);

  // useCallback so the reconciliation effect below can depend on it honestly rather
  // than omit it: it is called from inside that effect now, not from a button.
  const goToSigner = useCallback(
    (linkToken: string) => {
      navigate(`/esign/${linkToken}?return_to=${encodeURIComponent(SIGNING_RETURN_TO)}`);
    },
    [navigate],
  );

  // Ask the server what it knows, once, on arrival. This runs before the client presses
  // anything so the screen can say the true thing rather than discovering it mid-action.
  useEffect(() => {
    if (!partnerId || !coupleId) return;
    let abandoned = false;

    const reconcile = async () => {
      // The carried token is SENT rather than trusted. Resolving is not proof it belongs
      // to this run: a token from another client group, or one bound to an address the
      // client has since changed, resolves and reports `email_verified` identically. Only
      // the server can see the difference, and getting it wrong here means signing a
      // consent attributed to the wrong practice or the wrong address.
      const carried = readEsignLinkToken(coupleId, partnerId);
      let envelope: ConsentEnvelopeResponse;
      try {
        envelope = await requestConsentEnvelope(partnerId, {
          return_to: SIGNING_RETURN_TO,
          ...(carried ? { carried_token: carried } : {}),
        });
      } catch (caught) {
        if (abandoned) return;
        if (caught instanceof ApiError && caught.status === 409) {
          setMode("terminal");
          return;
        }
        // Transient. `unavailable` exists so the retry the copy promises is a control the
        // client can actually press.
        setError("The signing process didn't start.");
        setMode("unavailable");
        return;
      }
      if (abandoned) return;

      if (envelope.signed) {
        clearEsignLinkToken(coupleId, partnerId);
        // An already-signed envelope has nothing for this step to show either way, so it
        // is always a pass-through — but WHICH way through matters. Forward, that means
        // `onNext()` (the ordinary "already done, keep going" skip). Backward — a client
        // who tapped "back" from a later step — advancing forward again just bounces them
        // right back to where they started, which reads as "back" doing nothing. Continue
        // in the direction they were already moving instead.
        if (arrivedViaBack) {
          onBack();
        } else {
          onNext();
        }
        return;
      }

      // The server minted one (nothing pending, or what was pending no longer matches
      // this client's identity) — that token supersedes anything carried.
      if (envelope.link_token) {
        writeEsignLinkToken(coupleId, partnerId, envelope.link_token);
        goToSigner(envelope.link_token);
        return;
      }

      // Nothing minted, so a verified envelope is pending for this run. The server has
      // told us whether the token we hold is the one that addresses it.
      if (carried && envelope.carried_token_status) {
        goToSigner(carried);
        return;
      }
      clearEsignLinkToken(coupleId, partnerId);
      setMode("lost_link");
    };

    void reconcile();
    return () => {
      abandoned = true;
    };
  }, [arrivedViaBack, attempt, coupleId, goToSigner, onBack, onNext, partnerId]);

  // The explicit recovery. `force_new` is what tells the server this is a client that
  // lost its link rather than one that never verified — the distinction it cannot make
  // from the request alone, and the one it records.
  const retry = () => {
    setError("");
    setMode("checking");
    setAttempt((previous) => previous + 1);
  };

  const startOver = async () => {
    if (!partnerId || !coupleId || starting) return;
    setStarting(true);
    setError("");
    try {
      const envelope = await requestConsentEnvelope(partnerId, {
        return_to: SIGNING_RETURN_TO,
        force_new: true,
      });
      if (envelope.signed) {
        clearEsignLinkToken(coupleId, partnerId);
        onNext();
        return;
      }
      if (!envelope.link_token) {
        setError("The signing process didn't start. Try again.");
        return;
      }
      writeEsignLinkToken(coupleId, partnerId, envelope.link_token);
      goToSigner(envelope.link_token);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        setMode("terminal");
        return;
      }
      setError("The signing process didn't start. Try again.");
    } finally {
      setStarting(false);
    }
  };

  // TERMINAL. The server refused in a way no retry and no client action can change,
  // and — this is the part that decides the copy — no THERAPIST action can change it
  // either. Both causes the server names are ours: a record with no client group is
  // legacy data from the prior product, and a signer with no email address is a client
  // the identity step should never have let through. Sending them to their therapist
  // would be sending them to someone with no lever, which is the same false promise
  // this review found on the "neither of these is me" screen.
  //
  // So: no retry control (there is nothing to retry), no back (every route forward from
  // an earlier step funnels straight back into this wall), and the one true fact the
  // client can act on — someone who CAN fix it has been told. The server tells them at
  // the moment it raises the 409 (`_raise_consent_terminal`), and that report is
  // unconditional: it is a log line first and a Slack ping second, so the sentence
  // stays true when Slack is down.
  if (mode === "terminal") {
    return (
      <StepShell currentStep={currentStepIndex} totalSteps={totalSteps}>
        <StepHeading title="Consent signing can't continue">
          Something on Dova's side is stopping this step, and it is not anything you
          did. The problem has been reported to Dova's engineering team. Nothing you
          have entered has been lost, and it is safe to close this window.
        </StepHeading>
      </StepShell>
    );
  }

  if (mode === "unavailable") {
    return (
      <StepShell
        showBack
        onBack={onBack}
        currentStep={currentStepIndex}
        totalSteps={totalSteps}
        footer={<Button onClick={retry}>Try again</Button>}
      >
        <StepHeading title="Your consent document didn't load">
          Something went wrong on Dova's side. Nothing you've entered has been lost.
          Try again.
        </StepHeading>
      </StepShell>
    );
  }

  if (mode === "lost_link") {
    return (
      <StepShell
        showBack
        onBack={onBack}
        currentStep={currentStepIndex}
        totalSteps={totalSteps}
        footer={
          <Button onClick={() => void startOver()} disabled={starting}>
            {starting ? "Sending a new code…" : "Send a new code"}
          </Button>
        }
      >
        <StepHeading title="A fresh code before you sign">
          You verified your email earlier, but this browser no longer has the secure
          link to that session. A fresh code will get you back in.
        </StepHeading>

        {error ? (
          <p role="alert" className="m-0 text-[14px] text-danger">
            {error}
          </p>
        ) : null}
      </StepShell>
    );
  }

  // WHAT IS LEFT IS THE WAIT. This step used to end on a "Review and sign" screen whose
  // only content described the screen it was about to open and a caveat about a session
  // that might have expired — a page between the client and the document that asked them
  // to agree to be shown something. Operator removed it: reconciliation now navigates
  // straight to the signer the moment it holds a usable token.
  //
  // The expiry caveat went with it. The signing surface handles that case itself now,
  // sending a fresh code and landing on the code screen, so warning about it in advance
  // was describing a branch the client will be walked through if it happens.
  //
  // The other modes keep their screens, because each is a state that needs a person:
  // `lost_link` needs the re-issue pressed, `terminal` and `unavailable` need reading.
  // Only `checking` reaches here, and only while the round trip is in flight.
  return (
    <StepShell
      showBack
      onBack={onBack}
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      footer={<Button disabled>Preparing your document…</Button>}
    >
      <StepHeading title="Preparing your document" />

      {error ? (
        <p role="alert" className="m-0 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </StepShell>
  );
}
